/**
 * escoposDaIntegracao.js — os escopos de API Key que valem em cada vertical (Onda 107)
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * A tela de Chaves de API oferecia oito escopos juntos, iguais em qualquer
 * instalação. Cada um, porém, abre rotas de uma vertical só — e um escopo da
 * vertical errada não falha: abre rotas que devolvem vazio. O caso que motivou
 * a onda foi `access:read` no Clube Social, cujo rótulo ("Acessos e eventos")
 * parecia universal.
 *
 * QUAIS escopos valem em cada vertical sai do mapa da Documentação da
 * Integração (`VERTICAIS`), e não de uma terceira lista: tela e documentação
 * deixam de poder divergir. O servidor tem a mesma regra em
 * `backend/src/services/integrationScopes.js`, e `onda107.test.js` (backend)
 * exige que os três digam a mesma coisa.
 *
 * O TEXTO de cada escopo mora aqui porque cada escopo pertence a uma vertical
 * só (e os de visitantes às duas): o rótulo não precisa variar.
 */
import { VERTICAIS } from './integrationDocs';

// As descrições dizem o que o escopo REALMENTE abre — a regra da Onda 65. Uma
// descrição menor que a permissão faz o administrador conceder mais poder do
// que pretende; `aviso` aparece em destaque, e só onde há algo que se conceda
// sem perceber.
export const DESCRICOES = {
  'club_members:read': {
    rotulo: 'Associados — Leitura',
    desc: 'Consultar associados e seus veículos, pelo código do associado',
  },
  'club_members:write': {
    rotulo: 'Associados — Escrita e carga',
    desc: 'Criar, atualizar e inativar associados e veículos; carga em lote de até 500 por chamada; abrir e encerrar sincronizações',
    aviso: 'Inclui o encerramento de sincronização completa, que INATIVA todo associado ausente da carga. Conceda apenas ao sistema de gestão que é dono do cadastro.',
  },
  'club_events:read': {
    rotulo: 'Eventos de reconhecimento — Leitura',
    desc: 'Consultar os eventos enviados ao sistema de gestão do clube e o estado de cada entrega',
  },
  'residents:read': {
    rotulo: 'Unidades e moradores — Leitura',
    desc: 'Consultar unidades, moradores e seus veículos, pelo id interno ou pelo código da unidade',
  },
  'residents:write': {
    rotulo: 'Unidades e moradores — Escrita e carga',
    desc: 'Criar e atualizar unidades, moradores e veículos; carga em lote de até 500 por chamada; abrir e encerrar sincronizações',
    aviso: 'Inclui o encerramento de sincronização completa, que INATIVA todo cadastro ausente da carga. Conceda apenas ao sistema de gestão que é dono do cadastro.',
  },
  'access:read': {
    rotulo: 'Acessos e eventos — Leitura',
    desc: 'Histórico de acessos de moradores e visitantes, e o estado da fila de eventos enviados ao sistema de gestão',
  },
  'visitors:read': {
    rotulo: 'Visitantes — Leitura',
    desc: 'Consultar o cadastro de visitantes e as autorizações de visita, com o anfitrião resolvido',
  },
  'visitors:write': {
    rotulo: 'Visitantes — Cadastro e autorização',
    desc: 'Cadastrar visitantes (uma vez, pelo código externo) e autorizar cada visita',
    aviso: 'Autorizar uma visita LIBERA a entrada daquela placa na janela informada. É a permissão que abre a cancela para quem não é morador nem associado.',
  },
  // Onda 166: a liberação da saída pelo sistema de pagamento do cliente.
  'exits:read': {
    rotulo: 'Liberação da saída — Leitura',
    desc: 'Consultar as autorizações de saída registradas pelo sistema de pagamento e se cada uma já foi usada',
  },
  // Onda 195: a placa lida pela câmera LPR externa (Alphadigi), repassada pelo serviço do cliente.
  'lpr:write': {
    rotulo: 'Câmera LPR externa — Envio das leituras',
    desc: 'Enviar a placa lida pela câmera LPR Alphadigi (o JSON da câmera, repassado igual), com foto e recorte',
    aviso: 'Cada leitura enviada vira um ACESSO registrado — entrada ou saída, conforme o IP da câmera — e segue as regras de morador, visitante e liberação. Conceda apenas ao serviço que recebe as câmeras do cliente.',
  },
  'exits:write': {
    rotulo: 'Liberação da saída — Registro do pagamento',
    desc: 'Registrar o pagamento de um ticket (autorização de saída) e cancelá-lo em caso de estorno',
    aviso: 'Registrar um pagamento LIBERA a saída daquela placa pelo prazo configurado — com a abertura automática ligada, abre a cancela ou o portão. Conceda apenas ao sistema de gestão que cobra o estacionamento.',
  },
};

const VISITANTES = ['visitors:read', 'visitors:write'];
const SAIDA = ['exits:read', 'exits:write'];
const LPR = ['lpr:write']; // Onda 195

/** O nome da vertical como o administrador o reconhece. */
const NOMES = {
  clube_social: 'Clube Social',
  condominio: 'Condomínio (residencial ou corporativo)',
  estacionamento: 'Estacionamento',
};

export function nomeDaVertical(vertical) {
  return NOMES[vertical] || 'desconhecida';
}

function comTexto(chave) {
  return { chave, ...DESCRICOES[chave] };
}

/**
 * Os escopos oferecidos nesta vertical, em blocos: o cadastro próprio e os
 * visitantes. Vazio quando a vertical não usa integração por chave
 * (Estacionamento) ou ainda não é conhecida.
 */
export function blocosDeEscopos(vertical) {
  const V = VERTICAIS[vertical];
  if (!V) return [];
  const proprios = [V.leituraCadastro, V.escritaCadastro, V.leituraEventos];
  return [
    {
      id: 'cadastro',
      titulo: vertical === 'clube_social'
        ? 'Associados e eventos do clube'
        : 'Unidades, moradores e eventos do condomínio',
      nota: `Só da vertical ${nomeDaVertical(vertical)}.`,
      escopos: proprios.map(comTexto),
    },
    {
      id: 'visitantes',
      titulo: 'Visitantes',
      nota: 'Valem no Clube Social e no Condomínio.',
      escopos: VISITANTES.map(comTexto),
    },
    {
      id: 'saida',
      titulo: 'Liberação da saída pelo pagamento',
      nota: 'Valem no Clube Social e no Condomínio, quando o cliente tem sistema próprio de cobrança do estacionamento e a liberação está ligada em Configurações › Integração.',
      escopos: SAIDA.map(comTexto),
    },
    {
      id: 'lpr',
      titulo: 'Câmera LPR externa',
      nota: 'Valem no Clube Social e no Condomínio, quando a placa é lida pela própria câmera (Alphadigi) e repassada à plataforma.',
      escopos: LPR.map(comTexto),
    },
  ];
}

/** Lista plana: é o que o servidor aceita nesta vertical. */
export function escoposValidos(vertical) {
  return blocosDeEscopos(vertical).flatMap(b => b.escopos.map(e => e.chave));
}

/**
 * A primeira chamada que o integrador vai copiar. O caminho sai da
 * documentação (`baseCadastro` + `recurso`), e não de uma string escrita aqui:
 * a de antes apontava para `/api/integration/residents` em qualquer vertical —
 * rota do condomínio, e ainda a antiga.
 */
export function exemploDeUso(vertical, chave, base = '') {
  const V = VERTICAIS[vertical];
  if (!V) return null;
  return `curl -H "X-API-Key: ${chave}" ${base}${V.baseCadastro}/${V.recurso}`;
}

export function exemploDeNome(vertical) {
  if (vertical === 'clube_social') return 'Ex.: Sistema de gestão do clube';
  if (vertical === 'condominio') return 'Ex.: Sistema de portaria, app do síndico';
  return 'Ex.: Sistema de gestão';
}
